import SidePanel from '@monofront/design-system/organisms/SidePanel';
import { IDaySlot } from '@monofront/types';
import { useTranslations } from 'next-intl';
import { formatDateToPart } from '@monofront/business/utils';
import { useState } from 'react';
import { toast } from 'react-toastify';
import HeartLoader from '@monofront/design-system/components/HeartLoader';

type TimeSlotsSidePanelProps = {
  isOpen: boolean;
  closePanel: () => void;
  timeSlots?: IDaySlot[];
  isLoading: boolean;
  hasError: boolean;
};

export const TimeSlotsSidePanel: React.FC<TimeSlotsSidePanelProps> = ({
  isOpen,
  closePanel,
  timeSlots = [],
  isLoading,
  hasError,
}) => {
  const tTimeSlots = useTranslations('DeliveryBar.TimeSlots');
  const tErrors = useTranslations('Errors');

  const [selectedDay, setSelectedDay] = useState<IDaySlot | null>(null);

  return (
    <SidePanel.SidePanel isOpen={isOpen} onClose={closePanel}>
      <SidePanel.SidePanelHeader onClose={closePanel}>
        <h2 className="tw-lf-title-sm">{tTimeSlots('headerTitle')}</h2>
      </SidePanel.SidePanelHeader>
      <SidePanel.SidePanelBody UNSAFE_className="!tw-p-4">
        {isLoading ? (
          <HeartLoader />
        ) : hasError ? (
          <p className="tw-lf-error-message">{tErrors('anErrorOccured')}</p>
        ) : (
          <>
            <h3 className="tw-lf-body-bold-md tw-mb-4">
              {tTimeSlots('availableDates')}
            </h3>
            <div className="tw-mb-6 tw-flex tw-flex-1 tw-flex-wrap tw-gap-2">
              {timeSlots.map((daySlot) => {
                const { day, month, weekday } = formatDateToPart(daySlot.date);
                const dayHasAvailableSlot = daySlot.slots.some(
                  (slot) => slot.availableCapacity > 0,
                );

                const isSelected = selectedDay === daySlot;

                return (
                  <button
                    key={daySlot.date}
                    onClick={() => {
                      setSelectedDay(daySlot === selectedDay ? null : daySlot);
                    }}
                    className={`
                      tw-lf-button-clear
                      tw-flex
                      tw-h-[68px]
                      tw-w-[106px]
                      tw-flex-col
                      tw-items-center
                      tw-justify-center
                      tw-rounded-[4px]
                      tw-border
                      tw-border-solid
                      tw-px-2
                      tw-py-1
                      [&_span]:tw-lf-body-regular-md
                      disabled:tw-cursor-not-allowed
                      disabled:tw-border-transparent
                      disabled:tw-text-[var(--clr-grey-4)]
                      [&_span]:tw-capitalize
                      [&_span]:tw-leading-5
                      ${
                        isSelected
                          ? 'tw-border-beige-200'
                          : 'tw-border-blue-200'
                      }
                      ${isSelected ? 'tw-bg-beige-200' : ''}
                    `}
                    disabled={!dayHasAvailableSlot}
                  >
                    <span>{weekday.replace('.', '')}</span>
                    <span>{day}</span>
                    <span>{month.replace('.', '')}</span>
                  </button>
                );
              })}
            </div>
            {selectedDay ? (
              <>
                <h3 className="tw-lf-body-bold-md tw-mb-4">
                  {tTimeSlots('availableSlots')}
                </h3>
                <div className="tw-mb-6 tw-flex tw-flex-1 tw-flex-wrap tw-gap-2">
                  {selectedDay?.slots.map((slot) => {
                    const isAvailable = slot.availableCapacity !== 0;

                    return (
                      <button
                        key={slot.value}
                        className={`
                          tw-lf-button-clear
                          tw-flex
                          tw-h-[36px]
                          tw-w-[113px]
                          tw-flex-col
                          tw-items-center
                          tw-justify-center
                          tw-rounded-[4px]
                          tw-border
                          tw-border-solid
                          tw-border-blue-200
                          tw-px-2
                          tw-py-1
                          [&_span]:tw-lf-body-regular-md
                          disabled:tw-cursor-not-allowed
                          disabled:tw-border-transparent
                          disabled:tw-text-[var(--clr-grey-4)]
                          [&_span]:tw-capitalize
                          [&_span]:tw-leading-5
                        `}
                        disabled={!isAvailable}
                        onClick={() => {
                          toast(
                            <>
                              <span className="tw-lf-body-bold-md">
                                {tTimeSlots.rich('toastMessage1')}
                              </span>
                              <br />
                              <span className="tw-lf-body-regular-md">
                                {tTimeSlots.rich('toastMessage2')}
                              </span>
                            </>,
                          );
                        }}
                      >
                        {slot.value}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : null}
          </>
        )}
      </SidePanel.SidePanelBody>
    </SidePanel.SidePanel>
  );
};
